import React, { useMemo } from 'react';

import cs from 'classnames';
import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { deepEqual, ia } from 'lib/helpers/utility';

import { currentPractice } from 'components/practice/practiceState';
import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Allowed from 'components/shared/Permissions/Allowed';

import { getPayScheduleChanges } from '../../lib/helpers';

import CodingSection from './components/CodingSection/CodingSection';
import NdcTable from './components/CodingSection/components/NdcTable';
import DetailsSection from './components/DetailsSection/DetailsSection';
import MainSection from './components/MainSection/MainSection';
import PayerTable from './components/PayerTable/PayerTable';
import PricingSection from './components/PricingSection/PricingSection';
import {
  getFinalShapeServiceObj,
  getInitialValues,
  getServiceValidationSchema
} from './lib/helpers';

const NewServiceModal = ({
  service,
  isOpen,
  onClose = () => {},
  onSave = async (_service) => {},
  isLoading = false,
  parentTitle = null
}) => {
  const initialValues = getInitialValues(service);
  const practice = useRecoilValue(currentPractice);
  const isDental = practice?.kind === 'dental';
  const validationSchema = useMemo(() => getServiceValidationSchema(isDental), [isDental]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const serviceToBeCreated = getFinalShapeServiceObj(values);
      const payScheduleChanges = getPayScheduleChanges(
        values.paySchedule,
        service?.payerConnection
      );
      await onSave(serviceToBeCreated, payScheduleChanges);
    }
  });

  const isFormChanged = !deepEqual(formik.values, initialValues);

  const modalTitle = service?.id ? 'Edit service' : 'Create new service';

  const titleContent = parentTitle ? (
    <div className="flex items-center gap-2">
      <Icon
        icon="arrow-left-linear"
        size={18}
        className="cursor-pointer text-neutral-600"
        onClick={onClose}
      />
      <span className="cursor-pointer text-neutral-600" onClick={onClose}>
        {parentTitle}
      </span>
      <span className="text-neutral-400">/</span>
      <span>{modalTitle}</span>
    </div>
  ) : (
    modalTitle
  );

  return (
    <Modal
      isOpen={isOpen}
      bodyClassName="flex justify-center flex-1"
      handleClose={onClose}
      width="medium"
      title={titleContent}
      footer={
        <div className={cs('flex w-full justify-end gap-3')}>
          <Button
            variant="tertiary"
            text="Back"
            onClick={onClose}
            id="closeServiceModalBtn"
            size="small"
          />
          <Button
            onClick={() => formik.submitForm()}
            text={service?.id ? 'Save changes' : 'Create new service'}
            id="createUpdateServiceBtn"
            data-qa="create-update-service-btn"
            isLoading={isLoading}
            isDisabled={!isFormChanged}
            size="small"
          />
        </div>
      }>
      <div className="flex h-full flex-col gap-4">
        {/* Section Header */}
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold text-neutral-900">
            {service?.id ? 'Edit service' : 'Add new service'}
          </span>
          <span className="text-sm text-neutral-600">
            Configure service details, coding, and pricing information.
          </span>
        </div>

        <MainSection formik={formik} service={service} />
        <CodingSection formik={formik} />
        <PricingSection formik={formik} />
        {ia(formik.values.ndc_codes) && <NdcTable formik={formik} />}
        <DetailsSection formik={formik} />
        {ia(formik.values.paySchedule) && (
          <Allowed requiredPermissions="payments.read">
            <PayerTable formik={formik} />
          </Allowed>
        )}
      </div>
    </Modal>
  );
};

export default NewServiceModal;
